import { FC, MutableRefObject, useRef, useState } from 'react';

import VerticalBoxToggle from '@components/vertical-box-toggle/VerticalBoxToggle';
import { AMPM_OPTIONS } from '@constants/time-picker';
import { getCurrentTimeString } from '@utils/date';
import { sanitizeNumberInput } from '@utils/generic';
import useOnClickOutside from '@utils/hooks';

interface TimePickerPopupProps {
  time: string;
  handleConfirm: (hour: string, minute: string, ampm: string) => void;
  onCancel: () => void;
  isOutsideClickRequired?: boolean;
  className?: string;
  defaultToCurrentTime?: boolean;
}

const TimePickerPopup: FC<TimePickerPopupProps> = ({
  handleConfirm,
  onCancel,
  time,
  isOutsideClickRequired,
  className,
  defaultToCurrentTime = false
}) => {
  const timepickerModalRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [initialHour, initialMinuteAmpm] = time.split(':');
  const [initialMinute, initialAmpm] = (initialMinuteAmpm || '').split(' ');
  // fall back to the current system time when enabled, else the static 01:00 AM
  const [fallbackHour, fallbackMinuteAmpm] = defaultToCurrentTime
    ? getCurrentTimeString().split(':')
    : ['01', '00 AM'];
  const [fallbackMinute, fallbackAmpm] = fallbackMinuteAmpm.split(' ');
  const [error, setError] = useState({
    hour: '',
    minute: ''
  });

  const [hour, setHour] = useState<string>(initialHour || fallbackHour);
  const [minute, setMinute] = useState<string>(initialMinute || fallbackMinute);
  const [ampm, setAmpm] = useState<string>(initialAmpm || fallbackAmpm);

  const onHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHour = sanitizeNumberInput(e.target.value);

    if (newHour.length > 2) return;
    if (newHour.length === 2 && parseInt(newHour, 10) > 12) return;
    setHour(newHour);
    setError({ ...error, hour: '' });
  };

  const onMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMinute = sanitizeNumberInput(e.target.value);

    if (newMinute.length > 2) return;
    if (newMinute.length === 2 && parseInt(newMinute, 10) > 59) return;
    setMinute(newMinute);
  };

  const onConfirm = () => {
    if (hour === '0' || hour === '00') {
      setError({ ...error, hour: 'Hour must be greater than 0' });

      return;
    }

    handleConfirm(hour, minute, ampm);
  };

  useOnClickOutside(timepickerModalRef, (e) => {
    if (isOutsideClickRequired && onCancel) {
      e.stopPropagation();
      onCancel();
    }
  });

  return (
    <div
      ref={timepickerModalRef}
      className={`absolute bottom-16 right-0 z-50 size-fit min-h-[243px] rounded-2xl bg-white py-6 shadow-timePickerShadow ${
        className || ''
      }`}
    >
      <div>
        <div className="mb-2.5 px-6 text-sm font-medium text-content">
          Enter time
        </div>
        <div className="flex flex-col border-y border-grey30 px-6 py-4">
          <div className="flex items-start">
            <div className="flex flex-col">
              <input
                id="hour-input"
                value={hour}
                onChange={onHourChange}
                className="h-18 w-24 rounded-lg border-primary px-5 py-2.5 text-40 text-content outline-none focus:border-1.5"
              />
              <label
                htmlFor="hour-input"
                className="mt-2 text-xs text-darkGrey"
              >
                Hour
              </label>
            </div>

            <div className="flex h-18 w-6 items-center justify-center text-6xl text-content">
              :
            </div>
            <div className="mr-3 flex flex-col">
              <input
                id="minute-input"
                value={minute}
                onChange={onMinuteChange}
                className="h-18 w-24 rounded-lg border-primary px-5 py-2.5 text-40 text-content outline-none focus:border-1.5"
              />
              <label
                htmlFor="minute-input"
                className="mt-2 text-xs text-darkGrey"
              >
                Minute
              </label>
            </div>
            <VerticalBoxToggle
              options={AMPM_OPTIONS}
              selectedValue={ampm}
              setSelectedValue={setAmpm}
            />
          </div>
          {error.hour && (
            <div className="pt-3 text-sm text-error">{error.hour}</div>
          )}
        </div>

        <div className="flex w-full justify-end">
          <div
            onClick={onCancel}
            role="presentation"
            className="cursor-pointer p-3 text-sm font-medium text-darkGrey"
          >
            Cancel
          </div>
          <div
            onClick={onConfirm}
            role="presentation"
            className="cursor-pointer p-3 text-sm font-medium text-primary"
          >
            OK
          </div>
        </div>
      </div>
    </div>
  );
};
export default TimePickerPopup;
